import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { PrintService } from '../../services/print.service';
import { ReportsService } from '../../services/reports.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

declare let $: any;

export type SrptDateRange =
	| 'Custom'
	| 'ThisWeek'
	| 'LastMonth'
	| 'ThisMonth'
	| 'ThisYear'
	| '12months'
	| 'ThisQuarter';

export type SrptSortColumn = 'br' | 'salesman' | 'salesmanname' | 'sales' | 'units';

@Component({
	selector: 'app-srpt',
	templateUrl: './srpt.component.html',
	styleUrls: ['./srpt.component.scss']
})
export class SrptComponent implements OnInit, OnDestroy {
	@ViewChild('orderView') orderStatusViewRef: ElementRef;
	@ViewChild('details') detailsRef: ElementRef;
	@ViewChild('salesFilter') salesmanRef: MatSelect;
	@ViewChild('branchFilter') branchesRef: MatSelect;

	orderResults: any = false;
	orderdetails: any = false;
	displaydetails: any = false;
	reportCriteria: {
		fromdate: string;
		todate: string;
		locs: any[];
		salesperson: any[];
		conbr: string;
		placedfrom: string;
		minSales?: number | null;
		minUnits?: number | null;
	} | null = null;

	color: any = 'blue';
	config: any = [];
	filename = 'Salesman_Sales_by_Branch_';
	sending = false;

	branches: any[] = [];
	salespeople: any[] = [];
	conbr: any = [{ label: 'Consolidated', value: 'C' }, { label: 'Branch', value: 'B' }];
	placedfrom: any = [{ label: 'Included', value: '' }, { label: 'Excluded', value: '1' }, { label: 'Only', value: '2' }];

	dateRangeCtrl = new UntypedFormControl('ThisMonth' as SrptDateRange);
	fromDateCtrl = new UntypedFormControl(null);
	toDateCtrl = new UntypedFormControl(null);
	conBrRef = new UntypedFormControl('C');
	placedFromRef = new UntypedFormControl('');
	branchRef = new UntypedFormControl([]);
	salespersonRef = new UntypedFormControl([]);
	minSalesCtrl = new UntypedFormControl('');
	minUnitsCtrl = new UntypedFormControl('');

	readonly dateRangeOptions: { value: SrptDateRange; label: string }[] = [
		{ value: 'Custom', label: 'Custom range' },
		{ value: 'ThisWeek', label: 'This week' },
		{ value: 'LastMonth', label: 'Last month' },
		{ value: 'ThisMonth', label: 'This month' },
		{ value: 'ThisYear', label: 'This year (YTD)' },
		{ value: '12months', label: 'Past 12 months' },
		{ value: 'ThisQuarter', label: 'This quarter' }
	];

	sortBr: {
		[br: string]: { column: SrptSortColumn; desc: boolean };
	} = {};

	private spSub?: Subscription;
	private userSub?: Subscription;

	constructor(
		public globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private globalsService: GlobalsService,
		private reportsService: ReportsService,
		private location: Location,
		private modalService: NgbModal,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.configsubscription.subscribe((r) => {
			this.config = r;
		});
		this.globalSearchService.mesagesallowed.next(false);

		const end = new Date();
		const start = new Date(end.getFullYear(), end.getMonth(), 1);
		this.fromDateCtrl.setValue(start);
		this.toDateCtrl.setValue(end);

		this.reportsService.getBranches().subscribe((results: any) => {
			this.branches = results || [];
			this.applySalesmanDefaultFilters();
			this.cdr.markForCheck();
		});
		this.spSub = this.reportsService.getSalesPeople().subscribe((results: any) => {
			this.salespeople = results || [];
			this.applySalesmanDefaultFilters();
			this.cdr.markForCheck();
		});
		this.userSub = this.globalSearchService.user.subscribe(() => this.applySalesmanDefaultFilters());
	}

	ngOnDestroy(): void {
		this.spSub?.unsubscribe();
		this.userSub?.unsubscribe();
	}

	private applySalesmanDefaultFilters(): void {
		const auth = this.globalSearchService.user.getValue();
		const u = auth?.user;
		if (!u?.issalesman) {
			return;
		}
		const smList = (this.salespersonRef.value || []) as string[];
		if (!smList.length && this.salespeople.length) {
			const sm = String(u.salesman ?? '').trim();
			if (sm && this.salespeople.some((sp) => String(sp?.salesmancode ?? '').trim() === sm)) {
				this.salespersonRef.setValue([sm], { emitEvent: false });
			}
		}
		const locList = (this.branchRef.value || []) as string[];
		if (!locList.length && this.branches.length) {
			const dl = u.defaultlocation;
			const rawLoc =
				dl && typeof dl === 'object' && dl.loccode != null ? String(dl.loccode).trim() : '';
			if (rawLoc && this.branches.some((b) => String(b.loccode).trim() === rawLoc)) {
				this.branchRef.setValue([rawLoc], { emitEvent: false });
			}
		}
		this.cdr.markForCheck();
	}

	get salesmanTriggerLabel(): string {
		const codes: string[] = (this.salespersonRef.value || []) as string[];
		if (!codes.length) {
			return '';
		}
		if (this.salespeople.length && codes.length === this.salespeople.length) {
			return `All salespeople (${codes.length})`;
		}
		const names = codes
			.map((c) => this.salespeople.find((sp) => sp.salesmancode === c)?.salesmanname)
			.filter(Boolean) as string[];
		if (names.length <= 3) {
			return names.join(', ');
		}
		return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
	}

	get branchTriggerLabel(): string {
		const codes: string[] = (this.branchRef.value || []) as string[];
		if (!codes.length) {
			return '';
		}
		if (this.branches.length && codes.length === this.branches.length) {
			return `All branches (${codes.length})`;
		}
		const names = codes
			.map((c) => this.branches.find((b) => b.loccode === c)?.locationname)
			.filter(Boolean) as string[];
		if (names.length <= 3) {
			return names.join(', ');
		}
		return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
	}

	private formatYmd(v: any): string {
		if (!v) {
			return '';
		}
		const d = v instanceof Date ? v : new Date(v);
		if (Number.isNaN(d.getTime())) {
			return '';
		}
		return d.toISOString().split('T')[0];
	}

	private resolveQueryDates(): { fromdate: string; todate: string } | null {
		const range = (this.dateRangeCtrl.value || 'ThisMonth') as SrptDateRange;
		if (range === 'Custom') {
			const fromdate = this.formatYmd(this.fromDateCtrl.value);
			const todate = this.formatYmd(this.toDateCtrl.value);
			if (!fromdate || !todate) {
				return null;
			}
			return { fromdate, todate };
		}
		const end = new Date();
		end.setHours(0, 0, 0, 0);
		let start = new Date(end);
		switch (range) {
			case 'ThisWeek': {
				const dow = end.getDay();
				const back = dow === 0 ? 6 : dow - 1;
				start = new Date(end);
				start.setDate(end.getDate() - back);
				break;
			}
			case 'LastMonth':
				start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
				end.setTime(new Date(end.getFullYear(), end.getMonth(), 0).getTime());
				break;
			case 'ThisMonth':
				start = new Date(end.getFullYear(), end.getMonth(), 1);
				break;
			case 'ThisYear':
				start = new Date(end.getFullYear(), 0, 1);
				break;
			case '12months':
				start = new Date(end);
				start.setFullYear(start.getFullYear() - 1);
				start.setDate(start.getDate() + 1);
				break;
			case 'ThisQuarter': {
				const q = Math.floor(end.getMonth() / 3);
				start = new Date(end.getFullYear(), q * 3, 1);
				break;
			}
			default:
				start = new Date(end.getFullYear(), end.getMonth(), 1);
		}
		return { fromdate: this.formatYmd(start), todate: this.formatYmd(end) };
	}

	private parseOptionalPositiveFloat(ctrl: UntypedFormControl): number | null {
		const raw = ctrl.value;
		if (raw === '' || raw === null || raw === undefined) {
			return null;
		}
		const n = parseFloat(String(raw));
		if (!Number.isFinite(n) || n < 0) {
			return null;
		}
		return n;
	}

	get hasResults(): boolean {
		return !!(this.orderResults && this.orderResults.lines && Object.keys(this.orderResults.lines).length);
	}

	get branchKeysSorted(): string[] {
		if (!this.orderResults?.lines) {
			return [];
		}
		const keys = Object.keys(this.orderResults.lines);
		return keys.sort((a, b) => {
			const na = this.getBranchName(a);
			const nb = this.getBranchName(b);
			return String(na).localeCompare(String(nb), undefined, { sensitivity: 'base' });
		});
	}

	get grandTotals(): { sales: number; units: number } {
		const sales = +(this.orderResults?.total ?? 0);
		const units = +(this.orderResults?.totalunits ?? 0);
		return { sales, units };
	}

	get locationBlockCount(): number {
		return this.branchKeysSorted.length;
	}

	get dataRowCount(): number {
		if (!this.orderResults?.lines) {
			return 0;
		}
		let n = 0;
		for (const k of Object.keys(this.orderResults.lines)) {
			n += this.orderResults.lines[k]?.l?.length ?? 0;
		}
		return n;
	}

	sortedLinesForBranch(br: string, lines: any[]): any[] {
		if (!lines?.length) {
			return lines || [];
		}
		const st = this.sortBr[br];
		if (!st) {
			return [...lines];
		}
		const mul = st.desc ? -1 : 1;
		const out = [...lines];
		out.sort((a, b) => {
			let cmp = 0;
			switch (st.column) {
				case 'br':
					cmp = String(a.BR ?? '').localeCompare(String(b.BR ?? ''));
					break;
				case 'salesman':
					cmp = String(a.Salesman ?? '').localeCompare(String(b.Salesman ?? ''));
					break;
				case 'salesmanname':
					cmp = String(a.Salesmanname ?? '').localeCompare(String(b.Salesmanname ?? ''));
					break;
				case 'sales':
					cmp = (+a.Sales || 0) - (+b.Sales || 0);
					break;
				case 'units':
					cmp = (+(a.Units ?? 0) || 0) - (+(b.Units ?? 0) || 0);
					break;
				default:
					cmp = 0;
			}
			return cmp * mul;
		});
		return out;
	}

	setBranchSort(br: string, column: SrptSortColumn): void {
		const cur = this.sortBr[br];
		if (cur?.column === column) {
			this.sortBr[br] = { column, desc: !cur.desc };
		} else {
			this.sortBr[br] = {
				column,
				desc: column === 'sales' || column === 'units'
			};
		}
	}

	sortIcon(br: string, column: SrptSortColumn): string {
		const st = this.sortBr[br];
		if (!st || st.column !== column) {
			return 'unfold_more';
		}
		return st.desc ? 'arrow_downward' : 'arrow_upward';
	}

	public getCRPT(): void {
		if (!this.branchRef.value?.length) {
			this.showNotification('Please select at least one branch', 'danger', 'top', 'center');
			return;
		}
		if (!this.salespersonRef.value?.length) {
			this.showNotification('Please select at least one salesperson', 'danger', 'top', 'center');
			return;
		}
		const dates = this.resolveQueryDates();
		if (!dates) {
			this.showNotification('Please choose valid dates', 'danger', 'top', 'center');
			return;
		}
		const minSales = this.parseOptionalPositiveFloat(this.minSalesCtrl);
		const minUnits = this.parseOptionalPositiveFloat(this.minUnitsCtrl);
		if (this.minSalesCtrl.value !== '' && minSales === null) {
			this.showNotification('Minimum sales must be a non-negative number', 'warning', 'top', 'center');
			return;
		}
		if (this.minUnitsCtrl.value !== '' && minUnits === null) {
			this.showNotification('Minimum units must be a non-negative number', 'warning', 'top', 'center');
			return;
		}

		this.orderResults = false;
		this.sortBr = {};
		this.reportCriteria = {
			fromdate: dates.fromdate,
			todate: dates.todate,
			locs: this.branchRef.value,
			conbr: this.conBrRef.value,
			placedfrom: this.placedFromRef.value,
			salesperson: this.salespersonRef.value,
			minSales: minSales != null && minSales > 0 ? minSales : null,
			minUnits: minUnits != null && minUnits > 0 ? minUnits : null
		};

		const payload: any = { ...this.reportCriteria };
		if (payload.minSales == null) {
			delete payload.minSales;
		}
		if (payload.minUnits == null) {
			delete payload.minUnits;
		}

		this.sending = true;
		this.reportsService.getSRPT(payload).subscribe({
			next: (r) => {
				this.orderResults = r;
				this.sending = false;
				this.cdr.markForCheck();
			},
			error: () => {
				this.sending = false;
				this.cdr.markForCheck();
			}
		});
	}

	viewCustomer(sp: any, loccode: any): void {
		if (!this.reportCriteria) {
			return;
		}
		const drilldown = {
			sp,
			loc: loccode,
			fromdate: this.reportCriteria.fromdate,
			todate: this.reportCriteria.todate,
			locs: this.reportCriteria.locs,
			conbr: this.reportCriteria.conbr,
			placedfrom: this.reportCriteria.placedfrom,
			salesperson: this.reportCriteria.salesperson
		};
		this.reportsService.getSRPTDrill(drilldown).subscribe((order: any) => {
			this.orderdetails = order;
			this.modalService
				.open(this.orderStatusViewRef, { ariaLabelledBy: 'modal-title', size: 'xl' })
				.result.then(
					() => {},
					() => {}
				);
		});
	}

	openDocument(transaction: any, content: any, display: string): void {
		switch (transaction.DocType) {
			case 'Credit':
				this.globalsService.getCreditNote(transaction.id, display).subscribe((result: any) => {
					this.displaydetails = result;
					this.modalService
						.open(content, { ariaLabelledBy: 'modal-title', size: 'xl' })
						.result.then(
							() => {},
							() => {}
						);
				});
				break;
			case 'Invoice':
				this.globalsService.getInvoice(transaction.id, display).subscribe((result: any) => {
					this.displaydetails = result;
					this.modalService
						.open(content, { ariaLabelledBy: 'modal-title', size: 'xl' })
						.result.then(
							() => {},
							() => {}
						);
				});
				break;
		}
	}

	exportPdf(): void {
		if (!this.hasResults) {
			return;
		}
		const now = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.buildExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: `Salesman Sales by Branch ${this.reportCriteria?.fromdate} – ${this.reportCriteria?.todate} @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}

	getReportSubtitle(): string {
		const c = this.reportCriteria;
		let out = '';

		c?.conbr == 'C' ? (out += 'Consolidated report ') : (out += 'Branch report ');

		if (c?.locs?.length && c.locs.length !== this.branches.length) {
			out += 'For location(s): ';
			c.locs.forEach((loc) => (out += loc + ' '));
		} else {
			out += 'For all locations ';
		}

		if (c?.salesperson?.length && c.salesperson.length !== this.salespeople.length) {
			out += 'and salesperson(s): ';
			c.salesperson.forEach((person) => (out += person + ' '));
		} else {
			out += 'and all salespeople ';
		}

		if (c?.placedfrom === '') {
			out += 'including web orders';
		}
		if (c?.placedfrom === '1') {
			out += 'excluding web orders';
		}
		if (c?.placedfrom === '2') {
			out += 'web orders only';
		}
		if (c?.minSales != null && c.minSales > 0) {
			out += ` · min sales $${c.minSales}`;
		}
		if (c?.minUnits != null && c.minUnits > 0) {
			out += ` · min units ${c.minUnits}`;
		}
		return out.trim();
	}

	selectAllToggle(filter_name: string): void {
		let filter: MatSelect;
		switch (filter_name) {
			case 'salesman':
				filter = this.salesmanRef;
				break;
			case 'branch':
				filter = this.branchesRef;
				break;
			default:
				return;
		}
		const selected = filter.options.find((opt) => opt.selected);
		if (selected) {
			filter.options.forEach((item: MatOption) => item.deselect());
		} else {
			filter.options.forEach((item: MatOption) => item.select());
		}
	}

	getBranchName(loc: any): string {
		if (loc == 99 || loc === '99') {
			return 'ALL';
		}
		const branch = this.branches.filter((i) => i.loccode == loc);
		return branch[0]?.locationname ?? String(loc);
	}

	exportXls(): void {
		if (!this.hasResults) {
			return;
		}
		const now = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.buildExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: `Salesman Sales by Branch ${this.reportCriteria?.fromdate} – ${this.reportCriteria?.todate} @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}

	private esc(s: string): string {
		return String(s ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
	}

	buildExportTableHtml(): string {
		if (!this.orderResults?.lines) {
			return '';
		}
		const fmt = (n: number) =>
			(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		const fmtU = (n: number) =>
			(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

		let html = `<table class="table" style="width:100%;border-collapse:collapse">
			<thead><tr>
				<th style="text-align:left">Branch</th>
				<th style="text-align:left">BR</th>
				<th style="text-align:left">Sales ID</th>
				<th style="text-align:left">Sales name</th>
				<th style="text-align:right">Sales</th>
				<th style="text-align:right">Units</th>
			</tr></thead><tbody>`;

		for (const brKey of this.branchKeysSorted) {
			const block = this.orderResults.lines[brKey];
			const title = this.esc(this.getBranchName(brKey));
			html += `<tr><td colspan="6" style="font-weight:bold;background:#eceff1">${title}</td></tr>`;
			const lines = this.sortedLinesForBranch(brKey, block?.l || []);
			for (const line of lines) {
				html += `<tr>
					<td></td>
					<td>${this.esc(String(line.BR ?? ''))}</td>
					<td>${this.esc(String(line.Salesman ?? ''))}</td>
					<td>${this.esc(String(line.Salesmanname ?? ''))}</td>
					<td style="text-align:right">${fmt(+line.Sales)}</td>
					<td style="text-align:right">${fmtU(+(line.Units ?? 0))}</td>
				</tr>`;
			}
			html += `<tr>
				<td></td><td colspan="2"></td>
				<td style="font-weight:bold">Subtotal</td>
				<td style="text-align:right;font-weight:bold">${fmt(+block.total)}</td>
				<td style="text-align:right;font-weight:bold">${fmtU(+(block.totalunits ?? 0))}</td>
			</tr>`;
		}

		html += `<tr>
			<td colspan="3"></td>
			<td style="font-weight:bold">Report total</td>
			<td style="text-align:right;font-weight:bold">${fmt(+this.orderResults.total)}</td>
			<td style="text-align:right;font-weight:bold">${fmtU(+(this.orderResults.totalunits ?? 0))}</td>
		</tr></tbody></table>`;
		return html;
	}

	back(): void {
		this.location.back();
	}

	showNotification(message: string, type: string, from: string, align: string): void {
		$.notify(
			{
				icon: 'notifications',
				message
			},
			{
				type,
				timer: 2000,
				placement: { from, align },
				template:
					'<div data-notify="container" class="col-xl-4 col-lg-4 col-11 col-sm-4 col-md-4 alert alert-{0} alert-with-icon" role="alert">' +
					'<button mat-button type="button" aria-hidden="true" class="close mat-button" data-notify="dismiss"><i class="material-icons">close</i></button>' +
					'<i class="material-icons" data-notify="icon">notifications</i> ' +
					'<span data-notify="title">{1}</span> ' +
					'<span data-notify="message">{2}</span>' +
					'<div class="progress" data-notify="progressbar">' +
					'<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
					'</div></div>'
			}
		);
	}
}
